import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MapPin, Pencil, Plus, Trash2 } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Button, Empty, ErrorBlock, Input, Spinner } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { api } from "../../api/client.ts";
import { keys, useAddresses, useMe, useSaveAddress, type Address } from "../../api/queries.ts";
import { RegionPicker } from "../../components/region-picker.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 收货地址
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **改一条地址时，其余地址仍然看得见。**
 *    手机版是"整页换成表单"——屏幕只有那么大，只能这样。
 *    但地址这件事经常是**对着别的地址改**（"哪条是工地那个来着"），
 *    网页上有空间就不该把上下文藏起来：表单展开在列表上方，列表照旧在下面。
 *
 * ⚠️ 列表排成两列卡片，末尾放一张虚线的"新增"卡 ——
 *    手机版把新增钉在屏幕底部是因为列表长了要划到底，网页上一屏就看完了。
 */

type Draft = Omit<Address, "id"> & { id?: number };

const EMPTY: Draft = {
  receiver: "",
  phone: "",
  province: "",
  city: "",
  district: "",
  detail: "",
  isDefault: false,
};

/** 手机号简单校验。⚠️ 只挡明显填错的，真正的格式校验在服务端（zod）。 */
const PHONE_OK = (v: string) => /^1[3-9]\d{9}$/.test(v.trim());

function AddressForm({
  draft,
  onCancel,
  onSaved,
  isFirst,
}: {
  draft: Draft;
  onCancel: () => void;
  onSaved: () => void;
  isFirst: boolean;
}) {
  const [form, setForm] = useState<Draft>(draft);
  const save = useSaveAddress();

  const set = (k: keyof Draft, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const phoneBad = form.phone.trim() !== "" && !PHONE_OK(form.phone);
  const ready =
    form.receiver.trim() &&
    PHONE_OK(form.phone) &&
    form.province.trim() &&
    form.city.trim() &&
    form.district.trim() &&
    form.detail.trim();

  return (
    <div className="mb-5 rounded-xl border border-brand-200 bg-card p-5">
      <p className="mb-4 text-[15px] font-medium">{form.id ? "编辑地址" : "新增收货地址"}</p>

      {/* ⚠️ 网页上一行放得下四格：收货人 / 手机号 / 省 / 市 / 区 不用挤成两行。 */}
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="收货人"
          value={form.receiver}
          onChange={(e) => set("receiver", e.target.value)}
        />
        <Input
          label="手机号"
          inputMode="numeric"
          value={form.phone}
          onChange={(e) => set("phone", e.target.value)}
          error={phoneBad ? "手机号格式不对" : undefined}
        />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        <RegionPicker value={form} onChange={(r) => setForm({ ...form, ...r })} withLabels />
      </div>
      {/*
        ⚠️ 提示里要点名"楼层"和"有没有电梯"——
           建材是大件配送，师傅到了楼下才发现是六楼没电梯，这一趟就白跑了。
      */}
      <div className="mt-3">
        <Input
          label="详细地址"
          placeholder="写清楼层、有无电梯，建材是大件配送"
          value={form.detail}
          onChange={(e) => set("detail", e.target.value)}
        />
      </div>

      {/*
        🔴 第一条地址不给这个开关：它必然是默认的（服务端也会兜底）。
           给一个点了没用的开关，只会让人以为自己点错了。
      */}
      {!isFirst && (
        <label className="mt-3 flex w-fit items-center gap-2 text-[14px]">
          <input
            type="checkbox"
            className="size-[18px] accent-brand-600"
            checked={form.isDefault}
            onChange={(e) => set("isDefault", e.target.checked)}
          />
          设为默认收货地址
        </label>
      )}

      {save.error && <ErrorBlock error={save.error} />}

      <div className="mt-5 flex gap-3">
        <Button
          loading={save.isPending}
          disabled={!ready}
          onClick={async () => {
            await save.mutateAsync(form);
            onSaved();
          }}
        >
          保存
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          取消
        </Button>
      </div>
    </div>
  );
}

export function DesktopAddresses() {
  const { data: me } = useMe();
  const { data, isPending, error } = useAddresses(Boolean(me));
  const qc = useQueryClient();

  /** null = 没在编辑；对象 = 正在编辑（没有 id 就是新增）。 */
  const [editing, setEditing] = useState<Draft | null>(null);
  /**
   * ⚠️ 删除要**二次确认**：地址是手打进去的，误删一条就得重新输一遍，
   *    而卡片上的删除按钮离编辑很近。用同一张卡里的内联确认，不弹窗。
   */
  const [confirmDel, setConfirmDel] = useState<number | null>(null);

  /** ⚠️ 用 `keys.addresses` —— 手写 `["addresses"]` 跟真正的 key 对不上，
   *     删除/设为默认之后列表**不会刷新**，看起来像操作没生效。 */
  const invalidate = () => qc.invalidateQueries({ queryKey: keys.addresses });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/auth/me/addresses/${id}`),
    onSuccess: () => {
      setConfirmDel(null);
      void invalidate();
    },
  });

  const setDefault = useMutation({
    /** ⚠️ 走同一个 upsert 接口，把整条地址原样回传 + isDefault=true。 */
    mutationFn: (a: Address) => api.post<Address>("/auth/me/addresses", { ...a, isDefault: true }),
    onSuccess: () => invalidate(),
  });

  /** 🔴 同手机版：查询被 disabled 时 `isPending` 恒为 true，会转一个永远不停的圈。 */
  if (!me)
    return (
      <AccountShell title="收货地址">
        <Empty title="登录后管理收货地址" action={{ label: "去登录", to: "/login" }} compact />
      </AccountShell>
    );
  if (isPending)
    return (
      <AccountShell title="收货地址">
        <Spinner compact />
      </AccountShell>
    );
  if (error)
    return (
      <AccountShell title="收货地址">
        <ErrorBlock error={error} />
      </AccountShell>
    );

  const list = data ?? [];

  return (
    <AccountShell title="收货地址">
      {editing && (
        <AddressForm
          /** ⚠️ key 换了才会重挂载 —— 不换的话点"编辑另一条"表单里还是上一条的值。 */
          key={editing.id ?? "new"}
          draft={editing}
          isFirst={list.length === 0}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
        />
      )}

      {list.length === 0 && !editing ? (
        <Empty
          title="还没有收货地址"
          hint="建材是大件配送，先把工地或家里的地址存好，下单时直接选"
          action={{ label: "新增收货地址", onClick: () => setEditing({ ...EMPTY }) }}
          compact
        />
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {list.map((a) => (
            <div
              key={a.id}
              className={cn(
                "flex flex-col rounded-xl border bg-card p-5",
                a.isDefault ? "border-brand-200" : "border-border",
              )}
            >
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 size-[18px] shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="text-[15px]">
                    {a.receiver}
                    <span className="ml-2 text-muted-foreground">{a.phone}</span>
                  </p>
                  <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground break-anywhere">
                    {a.province}
                    {a.city}
                    {a.district} {a.detail}
                  </p>
                </div>
                {a.isDefault && (
                  <span className="shrink-0 rounded bg-brand-50 px-1.5 py-0.5 text-[12px] text-brand-700">
                    默认
                  </span>
                )}
              </div>

              {confirmDel === a.id ? (
                <div className="mt-auto flex items-center gap-2 border-t border-border pt-3 text-[13px]">
                  <span className="flex-1 text-destructive">删除这条地址？</span>
                  <Button size="sm" variant="outline" onClick={() => setConfirmDel(null)}>
                    取消
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() => remove.mutate(a.id)}
                  >
                    删除
                  </Button>
                </div>
              ) : (
                <div className="mt-auto flex items-center gap-4 border-t border-border pt-3 text-[13px]">
                  {/*
                    ⚠️ 已经是默认的就不出这个按钮 —— 点了没有任何变化的按钮
                       会让人以为没点上，反复点。
                  */}
                  {a.isDefault ? (
                    <span className="flex-1 text-muted-foreground">下单时默认用这个</span>
                  ) : (
                    <button
                      className="flex-1 text-left text-brand-600 transition-opacity hover:opacity-70"
                      disabled={setDefault.isPending}
                      onClick={() => setDefault.mutate(a)}
                    >
                      设为默认
                    </button>
                  )}
                  <button
                    className="flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
                    onClick={() => setEditing({ ...a })}
                  >
                    <Pencil className="size-[15px]" />
                    编辑
                  </button>
                  <button
                    className="flex items-center gap-1 text-muted-foreground transition-colors hover:text-destructive"
                    onClick={() => setConfirmDel(a.id)}
                  >
                    <Trash2 className="size-[15px]" />
                    删除
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* ⚠️ 虚线的"新增"卡排在列表末尾 —— 网页上一屏看完，不用像手机那样钉在底部。 */}
          {!editing && (
            <button
              onClick={() => setEditing({ ...EMPTY })}
              className="flex min-h-[132px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border text-muted-foreground transition-colors hover:border-brand-300 hover:text-brand-600"
            >
              <Plus className="size-6" />
              <span className="text-[14px]">新增收货地址</span>
            </button>
          )}
        </div>
      )}

      {remove.error && <ErrorBlock error={remove.error} />}
      {setDefault.error && <ErrorBlock error={setDefault.error} />}
    </AccountShell>
  );
}
